import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Certificate, SeedCertificateListing } from '@mvcp/contracts';
import {
  getDevices,
  getVendors,
  importSeedCertificate,
  listFrameworks,
  listSeedCertificates,
} from '../api';
import { ConfirmModal } from './ConfirmModal';
import { deviceLabel } from './device-label';

/** The date portion of an ISO timestamp (`2027-01-31`), or an em-dash when absent. */
function shortDate(iso?: string): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toISOString().slice(0, 10);
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: SeedCertificateListing[] }
  | { kind: 'error'; message: string };

/**
 * Best-effort display labels resolved from existing shared-read / manager-scoped seams
 * (NOT part of the wire contract of a seed listing, which carries only ids). A lookup
 * failure or an unresolvable id simply falls back to the raw id — it never blocks the
 * catalog.
 */
interface Labels {
  frameworks: Record<string, { code: string; name: string }>;
  /** Keyed by `${subjectType}:${subjectId}` → a human subject label. */
  subjects: Record<string, string>;
}

/** The three clearly-distinguished per-row states (AC2). */
type RowState = 'importable' | 'unmatched' | 'imported';

/**
 * `importedCertificateId` wins over `matched`: a row already imported reads as imported
 * even if its subject also still resolves. Otherwise a matched-but-not-yet-imported row
 * is importable; an unmatched row is surfaced for review (AR-17 — never hidden).
 */
function rowStateOf(row: SeedCertificateListing): RowState {
  if (row.importedCertificateId) return 'imported';
  return row.matched ? 'importable' : 'unmatched';
}

/**
 * Row-state → pill tone + label. `imported` is deliberately NEUTRAL, never an "ok"/verified
 * tone: an imported certificate is born `pending` and follows the verification path — it is
 * never presented as trusted (AR-17).
 */
const STATE_META: Record<RowState, { tone: 'ok' | 'warn' | 'neutral'; label: string }> = {
  importable: { tone: 'ok', label: 'Matched' },
  unmatched: { tone: 'warn', label: 'Not in your directory' },
  imported: { tone: 'neutral', label: 'Imported' },
};

const SUBJECT_LABEL: Record<SeedCertificateListing['subjectType'], string> = {
  vendor: 'Vendor',
  device: 'Device',
};

/**
 * Compliance Manager cockpit → Certification catalog (Story 6.7, FR-13, AR-5/AR-17).
 * The pure-UI slice over the Story 3.5 seed-certification-database API: it BROWSES the
 * tenant-less seed catalog via `listSeedCertificates()` and IMPORTS one matched row into
 * a real (pending, `provenance='imported-from-seed'`) Certificate via
 * `importSeedCertificate(id)`. Each row shows one of three honest states — importable,
 * unmatched (surfaced for review, never hidden), or already-imported — and only an
 * importable row exposes an enabled Import action. Adds NO API/contract/migration/compute;
 * it consumes the existing `api.ts` seams only. Manager-cockpit-only (never the admin or
 * vendor shells) — the API Import guard is manager-only (belt with that guard).
 *
 * The framework code/name and subject label are enriched best-effort from the existing
 * `listFrameworks()`/`getVendors()`/`getDevices()` seams; an unresolvable id (e.g. an
 * unmatched subject, absent from the directory) falls back to the raw id. Framework labels
 * use the platform-GLOBAL `listFrameworks()` (not the maritime-filtered applicable set), so
 * a seed row referencing a framework outside the caller's applicable set still resolves.
 */
export function SeedCatalogView(): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [labels, setLabels] = useState<Labels>({ frameworks: {}, subjects: {} });
  const [pending, setPending] = useState<SeedCertificateListing | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Monotonic token guarding against a stale enrichment write: only the latest `enrich()`
  // invocation may commit its labels (replaces the single-effect `cancelled` guard now
  // that enrichment is re-runnable on every (re)load).
  const enrichToken = useRef(0);
  // In-session optimistic imports (seedId → created certificateId). A post-import reconcile
  // re-read can lag on ANY prior in-session import, not just the latest — so a whole-list
  // replace that re-annotated only the current seedId would revert an EARLIER imported row
  // to `importable` (re-offering Import) while its success is already established. Preserve
  // every in-session import's annotation until the server's own annotation lands.
  const optimisticImports = useRef<Record<string, string>>({});

  // Best-effort label enrichment. Runs alongside (never gates) the catalog load: each
  // lookup swallows its own failure so a single 403/500 can't blank the whole surface,
  // and every value has an id fallback. Consumes existing seams — no new endpoint.
  // Framework labels come from the platform-GLOBAL `listFrameworks()` so a seed row that
  // references a framework outside the caller's maritime-filtered applicable set still
  // resolves. Re-runnable: fires on mount AND on every (re)load so Retry heals labels too.
  const enrich = useCallback(async () => {
    const token = ++enrichToken.current;
    const [frameworks, vendors, devices] = await Promise.all([
      listFrameworks().catch(() => []),
      getVendors().catch(() => []),
      getDevices().catch(() => []),
    ]);
    // A newer enrich() superseded this one — drop the stale result.
    if (token !== enrichToken.current) return;
    const fw: Record<string, { code: string; name: string }> = {};
    for (const f of frameworks) fw[f.id] = { code: f.code, name: f.name };
    const subjects: Record<string, string> = {};
    for (const v of vendors) subjects[`vendor:${v.vendor.id}`] = v.vendor.name;
    // Story 8.5 (AD-25): a device subject is named model + revision, through the one
    // web owner — a seeded certificate must say which revision it covers.
    for (const d of devices) subjects[`device:${d.device.id}`] = `${deviceLabel(d.device)} (${d.vendor.name})`;
    setLabels({ frameworks: fw, subjects });
  }, []);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    // Kick off label enrichment alongside the catalog read (heals labels on Retry too).
    void enrich();
    try {
      const rows = await listSeedCertificates();
      setState({ kind: 'ready', rows });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, [enrich]);

  useEffect(() => {
    void load();
  }, [load]);

  const subjectKey = (row: SeedCertificateListing): string => `${row.subjectType}:${row.subjectId}`;

  // Deterministic sort (framework code, then subject, then id) so the list is stable
  // across reloads. Falls back to the raw ids until enrichment resolves.
  const rows = useMemo(() => {
    if (state.kind !== 'ready') return [];
    const fwLabel = (id: string): string => labels.frameworks[id]?.code ?? id;
    const subjLabel = (r: SeedCertificateListing): string => labels.subjects[subjectKey(r)] ?? r.subjectId;
    return [...state.rows].sort((a, b) => {
      const byFw = fwLabel(a.frameworkId).localeCompare(fwLabel(b.frameworkId));
      if (byFw !== 0) return byFw;
      const bySubj = subjLabel(a).localeCompare(subjLabel(b));
      if (bySubj !== 0) return bySubj;
      return a.id.localeCompare(b.id);
    });
  }, [state, labels]);

  function openImport(row: SeedCertificateListing): void {
    setActionError(null);
    setNotice(null);
    setPending(row);
  }

  async function onConfirmImport(): Promise<void> {
    if (!pending || busy) return;
    const seedId = pending.id;
    setActionError(null);
    setBusy(true);
    let created: Certificate | null = null;
    try {
      created = await importSeedCertificate(seedId);
    } catch (err: unknown) {
      // Fail closed with the API's legible message (e.g. 422 unmatched / 409 already
      // imported). Keep the confirm affordance open so the catalog stays open and no
      // duplicate is implied (AC4).
      setActionError(err instanceof Error ? err.message : "Couldn't import. Try again.");
      setBusy(false);
      return;
    }
    // Defensive: `unwrap` yields `body.data`, which can be null on a malformed `ok`
    // envelope. Treat a missing certificate as a failure — keep the modal open with an
    // inline error rather than proceeding to a success flip with no cert to show.
    if (!created || !created.id) {
      setActionError("The import didn't return a certificate. Try again.");
      setBusy(false);
      return;
    }
    const importedId = created.id;
    optimisticImports.current[seedId] = importedId;
    setBusy(false);
    setPending(null);
    setNotice(`Imported into a new certificate (${importedId}) — pending review.`);
    // Optimistically flip the just-imported row to the imported state WITHOUT resetting
    // to loading (no blank-table flash). A successful import must never be downgraded to
    // an error by a failed re-read.
    setState((s) =>
      s.kind === 'ready'
        ? {
            kind: 'ready',
            rows: s.rows.map((row) =>
              row.id === seedId ? { ...row, importedCertificateId: importedId } : row,
            ),
          }
        : s,
    );
    // Background reconcile: re-read to pick up the server's authoritative annotation, but
    // swallow a failure so it can't turn a successful import into an error state (AC3) —
    // the optimistically-patched row + success notice stay intact.
    try {
      const rows = await listSeedCertificates();
      // Preserve every in-session import's optimistic annotation if the re-read hasn't caught
      // up: on read-after-write lag the server may still report a just-imported seed WITHOUT
      // its `importedCertificateId`, which would otherwise revert the row to `importable` (and
      // re-offer Import) while its import already succeeded. This merges over ALL prior
      // in-session imports (`optimisticImports`), not just the current `seedId` — so a later
      // import's reconcile can no longer clobber an earlier imported row. Each row heals to
      // the server's own annotation the moment it lands.
      setState({
        kind: 'ready',
        rows: rows.map((row) =>
          !row.importedCertificateId && optimisticImports.current[row.id]
            ? { ...row, importedCertificateId: optimisticImports.current[row.id] }
            : row,
        ),
      });
    } catch {
      // Keep the optimistic state + success notice; the import already succeeded.
    }
  }

  return (
    <>
      <div>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
          Certification catalog
        </h1>
        <p className="muted" style={{ margin: 0 }}>
          Browse the seeded certification database and import a known certificate for a Vendor or
          Device in your directory. An imported certificate is created as pending and follows the
          normal verification path — it is never trusted automatically. Rows that don’t match your
          directory are shown for review.
        </p>
      </div>

      <section className="card stack" aria-labelledby="seed-catalog-heading">
        <h2 id="seed-catalog-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Seed certificates
        </h2>

        {state.kind === 'loading' && (
          <p className="muted" style={{ margin: 0 }} aria-busy="true">
            Loading…
          </p>
        )}

        {state.kind === 'error' && (
          <div className="stack" style={{ gap: 8 }} role="alert">
            <p className="muted" style={{ margin: 0 }}>{state.message}</p>
            <button
              type="button"
              className="btn-accent"
              data-surface="accent"
              onClick={() => void load()}
              style={{ alignSelf: 'flex-start' }}
            >
              Retry
            </button>
          </div>
        )}

        {state.kind === 'ready' && rows.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>The seed certification database is empty.</p>
        )}

        {state.kind === 'ready' && rows.length > 0 && (
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Framework</th>
                <th>Subject</th>
                <th>Source</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const rs = rowStateOf(r);
                const meta = STATE_META[rs];
                const fw = labels.frameworks[r.frameworkId];
                const subject = labels.subjects[subjectKey(r)] ?? r.subjectId;
                return (
                  <tr key={r.id} data-seed-row={r.id} data-row-state={rs}>
                    <td>
                      <span data-numeric>{fw ? fw.code : r.frameworkId}</span>
                      {fw && (
                        <span className="muted" style={{ marginLeft: 6, fontSize: 13 }}>
                          {fw.name}
                        </span>
                      )}
                    </td>
                    <td>
                      {subject}{' '}
                      <span className="muted" style={{ fontSize: 13 }}>{SUBJECT_LABEL[r.subjectType]}</span>
                    </td>
                    <td>
                      {r.source}
                      {r.approvingAuthority ? (
                        <span className="muted" style={{ fontSize: 13 }}> · {r.approvingAuthority}</span>
                      ) : null}
                    </td>
                    <td data-numeric>{shortDate(r.expiresAt)}</td>
                    <td>
                      <span className="status-pill" data-tone={meta.tone}>
                        <span className="status-pill__dot" aria-hidden="true" />
                        {meta.label}
                      </span>
                    </td>
                    <td>
                      {rs === 'importable' && (
                        <button
                          type="button"
                          className="btn-accent"
                          data-surface="accent"
                          aria-label={`Import ${fw ? fw.code : r.frameworkId} for ${subject}`}
                          onClick={() => openImport(r)}
                        >
                          Import
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        <div role="status" aria-live="polite" style={{ minHeight: 16 }}>
          {notice && (
            <p className="muted" style={{ margin: 0, fontSize: 13, wordBreak: 'break-all' }}>
              {notice}
            </p>
          )}
        </div>
      </section>

      {pending && (
        <ConfirmModal
          title="Import certificate"
          confirmLabel="Import"
          busy={busy}
          error={actionError}
          message={
            <div className="stack" style={{ gap: 8 }}>
              <p style={{ margin: 0 }}>
                Import{' '}
                <strong>{labels.frameworks[pending.frameworkId]?.name ?? pending.frameworkId}</strong>{' '}
                for{' '}
                <strong>{labels.subjects[subjectKey(pending)] ?? pending.subjectId}</strong> from{' '}
                {pending.source}.
              </p>
              <p style={{ margin: 0 }}>
                This creates a <strong>pending</strong> certificate that follows the normal
                verification path — it is not trusted automatically.
              </p>
            </div>
          }
          onConfirm={() => void onConfirmImport()}
          onCancel={() => {
            setPending(null);
            setActionError(null);
          }}
        />
      )}
    </>
  );
}
